import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
  Globe2,
  Filter,
} from "lucide-react";

import { getSpyRankBadgeClass, getSpyTrendBadgeClass } from "@/lib/spy-display";
import { EMPTY_SPYSERP_FILTER_OPTIONS } from "@/types/spyserp";
import { type DateRangeOption } from "@/components/clicks/DateRangeFilter";
import { CopyableValue, CopyIconButton } from "@/components/clicks/CopyableValue";
import { parseUrlActiveFilters } from "@/lib/url-active-filters";
import { useSpyserpFilterOptions } from "@/hooks/useSpyserpFilterOptions";
import { useSpyserpUrlStats } from "@/hooks/useSpyserpUrlStats";
import {
  toSpyserpSegmentFilterOptions,
  useSpyserpSegments,
} from "@/hooks/useSpyserpSegments";
import { SortableHead } from "./SortableHead";
import { SiteTypeBadge } from "./SiteTypeBadge";
import { SpySerpFiltersSidebar } from "./SpySerpFiltersSidebar";

export default function URLsTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("limit") || "15", 10);

  const validSortCols = [
    "keyword",
    "page_url",
    "domain",
    "location",
    "lang_page",
    "current_rank",
    "rank_change",
  ];
  const rawSortCol = searchParams.get("sort") || "current_rank";
  const sortCol = validSortCols.includes(rawSortCol)
    ? rawSortCol
    : "current_rank";
  const sortDesc = searchParams.get("desc") === "true";

  const filtersParam = searchParams.get("filters");
  const activeFilters = parseUrlActiveFilters(filtersParam);

  const dateRange = (searchParams.get("dateRange") ||
    "last_30_days") as DateRangeOption;
  const customFrom = searchParams.get("from") || undefined;
  const customTo = searchParams.get("to") || undefined;

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const { data: filterOptions = EMPTY_SPYSERP_FILTER_OPTIONS } =
    useSpyserpFilterOptions();
  const { data: segmentRows = [] } = useSpyserpSegments({
    enabled: isFiltersOpen,
  });
  const segmentFilterOptions = useMemo(
    () => toSpyserpSegmentFilterOptions(segmentRows),
    [segmentRows],
  );

  const { data, isPending } = useSpyserpUrlStats({
    page,
    pageSize,
    sortCol,
    sortDesc,
    filtersParam,
    activeFilters,
    dateRange,
    customFrom,
    customTo,
  });

  const stats = data?.stats ?? [];
  const totalCount = data?.totalCount ?? 0;
  const loading = isPending;

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      prev.set("page", newPage.toString());
      return prev;
    });
  };

  const handleLimitChange = (newLimit: string) => {
    setSearchParams((prev) => {
      prev.set("limit", newLimit);
      prev.set("page", "1");
      return prev;
    });
  };

  const handleSort = (column: string) => {
    setSearchParams((prev) => {
      if (sortCol === column) prev.set("desc", (!sortDesc).toString());
      else {
        prev.set("sort", column);
        prev.set("desc", column === "current_rank" ? "false" : "true");
      }
      prev.set("page", "1");
      return prev;
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const renderRankBadge = (rank: number) => {
    if (!rank || rank > 100) {
      return (
        <Badge variant="outline" className="badge-muted font-normal">
          {">100"}
        </Badge>
      );
    }
    return <Badge className={getSpyRankBadgeClass(rank)}>#{rank}</Badge>;
  };

  const renderTrend = (change: number) => {
    if (!change || change === 0) {
      return (
        <Badge variant="outline" className="font-normal bg-muted/30 text-muted-foreground">
          <Minus className="mr-1 h-3 w-3" /> Stable
        </Badge>
      );
    }
    const Icon = change > 0 ? TrendingUp : TrendingDown;
    return (
      <Badge variant="outline" className={getSpyTrendBadgeClass(change)}>
        <Icon className="mr-1 h-3 w-3" /> {change > 0 ? `+${change}` : change}
      </Badge>
    );
  };

  return (
    <div className="flex h-full w-full">
      <aside
        className={`flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
          isFiltersOpen ? "w-64 opacity-100 mr-6" : "w-0 opacity-0"
        }`}
      >
        <SpySerpFiltersSidebar
          availableOptions={filterOptions}
          segments={segmentFilterOptions}
          showSlugFilter={true}
        />
      </aside>

      <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="text-sm font-semibold">Keyword Rankings</h3>
            <p className="text-xs text-muted-foreground">
              Showing detailed URL performance
            </p>
          </div>

          <Button
            variant={isFiltersOpen ? "secondary" : "outline"}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className="gap-2 cursor-pointer"
          >
            <Filter className="w-4 h-4" />
            Filters
            {Object.keys(activeFilters).length > 0 && (
              <Badge variant="default" className="ml-1 px-1.5 h-5">
                {Object.keys(activeFilters).length}
              </Badge>
            )}
          </Button>
        </div>

        <div className="border rounded-md bg-card flex flex-col overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <SortableHead
                  label="Keyword"
                  column="keyword"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
                <SortableHead
                  label="URL"
                  column="page_url"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Domain"
                  column="domain"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
                <SortableHead
                  label="GEO"
                  column="location"
                  align="center"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Language"
                  column="lang_page"
                  align="center"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Position"
                  column="current_rank"
                  align="center"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Trend"
                  column="rank_change"
                  align="right"
                  currentSort={sortCol}
                  isSortDesc={sortDesc}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-10 text-muted-foreground"
                  >
                    Loading URL data...
                  </TableCell>
                </TableRow>
              ) : stats.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-10 text-muted-foreground"
                  >
                    No data found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                stats.map((row, idx) => (
                  <TableRow key={`${row.page_url}-${row.keyword}-${idx}`}>
                    <TableCell className="font-medium text-sm">
                      {row.keyword}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <a
                          href={row.page_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          {row.page_url.replace(/^https?:\/\//, "")}
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                        <CopyIconButton value={row.page_url} />
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <CopyableValue value={row.domain} noTruncate />
                        <SiteTypeBadge siteType={row.site_type} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <Globe2 className="w-3 h-3" /> {row.location || "N/A"}
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">
                      {row.lang_page || "N/A"}
                    </TableCell>
                    <TableCell className="text-center">
                      {renderRankBadge(row.current_rank)}
                    </TableCell>
                    <TableCell className="text-right">
                      {renderTrend(row.rank_change)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing</span>
            <Select
              value={pageSize.toString()}
              onValueChange={handleLimitChange}
            >
              <SelectTrigger className="w-[80px] h-9">
                <SelectValue placeholder="15" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">URLs</span>
          </div>

          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
}
